import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthContext, useLogoutDeletedAccount } from '../../../../../context/auth.context'
import { ButtonPrimaryConfirm } from '../../../../../shared/component/button'
import { Card } from '../../../../../shared/card/card'
import { useApiFormUserLogin } from '../../../../../api-new/state/content'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { IconDelete } from '../../../../../shared/icon/icon'
import { useNotificationContext } from '../../../../../context/notification/context'
import { isCode, refusedOrganizations, serverMessage } from '../../../../../shared/error/server-message'
import { withReference } from '../../../../../shared/error/error-notice'

// "Delete account", GitHub style: the last owner of a company organization
// can't delete their account until someone else is an owner or the
// organization is deleted. The content API's preflight
// (GET user-login/self-remove/preflight -> {lastOwnerOf}) names those
// organizations before anyone tries; the delete itself still answers 409
// account.last_owner if that changes in between.
export const DeleteAccount = () => {
  const logout = useLogoutDeletedAccount()

  const authContext = useAuthContext()

  const apiFormUserLogin = useApiFormUserLogin()

  const notificationContext = useNotificationContext()

  // null until the preflight answers; [] when nothing blocks.
  const [lastOwnerOf, setLastOwnerOf] = useState(null)

  useEffect(
    () => {
      let live = true
      apiFormUserLogin
        .get('self-remove/preflight')
        .then(result => live && setLastOwnerOf((result && result.lastOwnerOf) || []))
        // An older server has no preflight: the delete's own 409 still guards.
        .catch(() => live && setLastOwnerOf([]))
      return () => {
        live = false
      }
    },
    // api-new hooks have unstable identity: run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleConfirm = async () => {
    try {
      await apiFormUserLogin.delete('self-remove')
      logout()
    } catch (err) {
      if (isCode(err, 'account.last_owner')) setLastOwnerOf(refusedOrganizations(err))
      else notificationContext.toast.notify(withReference(serverMessage(err, 'Could not delete your account'), err), 5000)
    }
  }

  const blocked = lastOwnerOf === null || lastOwnerOf.length > 0

  const currentOrganizationID = authContext && authContext.state && authContext.state.organizationID

  return (
    <React.Fragment>
      <BreadcrumbView Icon={IconDelete} text="delete account">
        <Card title="delete account">
          {lastOwnerOf &&
            lastOwnerOf.length > 0 &&
            <div className="alert alert-warning m-2" role="alert">
              {lastOwnerOf.map(organization =>
                <p key={organization.id} className="mb-1">
                  You're the only owner of <strong>{organization.name}</strong>.{' '}
                  {organization.id === currentOrganizationID
                    ? <React.Fragment>
                        <Link to="/org/user/list">Make someone else an owner</Link> or{' '}
                        <Link to="/org/organization/delete">Delete the organization</Link>.
                      </React.Fragment>
                    : <React.Fragment>
                        <Link to="/auth/account">Switch to {organization.name}</Link> to make someone else an owner or delete the organization.
                      </React.Fragment>}
                </p>
              )}
            </div>}
          <div className="p-2">Warning, this action will delete your account and remove all data. This action cannot be redone.</div>
          <ButtonPrimaryConfirm
            text="Delete Account"
            disabled={blocked}
            confirmText="Are you sure you want to delete your account?"
            onConfirm={handleConfirm}
          />
        </Card>
      </BreadcrumbView>
    </React.Fragment>
  )
}
